import { useMemo, useState } from 'react'
import { cn } from "@/lib/utils"
import { Trash2, ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from "@/components/ui/button"
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { ChatStatusFlow } from './ChatStatusFlow'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
interface Message {
  senderType: 'user' | 'artist' | 'assistant'
  content: string
  timestamp: string | { seconds: number; nanoseconds: number }
  isRead: boolean
  type?: 'text' | 'image' | 'notification' | 'booking_selection' | 'simple_availability'
  notificationSent?: boolean
  notification?: {
    notificationId: string
    status: 'pending' | 'sent' | 'received' | 'clicked' | 'dismissed'
    sentAt?: Date
    receivedAt?: Date
    clickedAt?: Date
    dismissedAt?: Date
    deliveryAttempts?: number
    fcmResponseId?: string
  }
}

interface UserDetails {
  phoneNumber?: string
  fcmToken?: string
  iosNotificationsEnabled?: boolean
  smsNotificationsEnabled?: boolean
}

interface ArtistDetails {
  phoneNumber?: string
  fcmToken?: string
  notificationPermissionStatus?: string
  smsNotificationsEnabled?: boolean
}

interface Chat {
  id: string
  userId: string
  artistId: string
  createdAt: { seconds: number; nanoseconds: number }
  lastMessageAt: { seconds: number; nanoseconds: number } | null
  lastMessage: string | null
  messages: Message[]
  status: string
  artistName?: string
  hasLead?: boolean
  userInstagram?: string | null
  userEmail?: string | null
  artistDetails?: ArtistDetails
  userDetails?: UserDetails
  offerSentDate?: string | null
  offerAcceptedDate?: string | null
  appointmentDate?: string | null
  bookingPrice?: number | null
  aiBookingDetected?: boolean
  aiBookingDate?: string | null
  aiBookingPrice?: number | null
  platformExitLikelihood?: number
  bookingOnly?: boolean
}

interface BookingAnalysis {
  chatId: string
  isBooked: boolean
  confidence: number
  bookedTime?: string | null
  bookingType?: 'custom' | 'flash' | 'unknown'
  summary?: string
  reasoning?: string
}

interface ChatGridProps {
  chats: Chat[]
  statusFilter: string
  dateFilter: string
  sortOrder?: 'newest' | 'oldest'
  onDeleteChat: (chatId: string) => Promise<void>
  bookingAnalyses?: Map<string, BookingAnalysis>
  /** Full analytics chat list (e.g. all artists) so “Chats” can list every thread for this user. */
  allChatsForUserLookup?: Chat[]
  /** Switch artist scope and scroll the chat card into view in the list. */
  onNavigateToUserChat?: (payload: { chatId: string; artistId: string }) => void
}

type TimestampValue = string | { seconds: number; nanoseconds: number } | null | undefined
type TimestampLike = TimestampValue | Date | number | { toDate?: () => Date } | null | undefined

/** European event day for Time4Tats branding (9 May 2026). */
const TIME4TATS_EVENT_Y = 2026
const TIME4TATS_EVENT_M = 5
const TIME4TATS_EVENT_D = 9

function parseAppointmentCalendarDate(value: string): Date | null {
  const s = value.trim()
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
  if (ymd) {
    const y = Number(ymd[1])
    const mo = Number(ymd[2])
    const d = Number(ymd[3])
    const dt = new Date(y, mo - 1, d)
    return Number.isNaN(dt.getTime()) ? null : dt
  }
  const dt = new Date(s)
  return Number.isNaN(dt.getTime()) ? null : dt
}

function isTime4TatsEventDay(date: Date): boolean {
  return (
    date.getFullYear() === TIME4TATS_EVENT_Y &&
    date.getMonth() === TIME4TATS_EVENT_M - 1 &&
    date.getDate() === TIME4TATS_EVENT_D
  )
}

/** Milliseconds for sorting / grouping by real last activity (matches backend lastActive → lastMessageAt). */
function getChatActivityMs(chat: Chat): number {
  if (chat.lastMessageAt?.seconds != null) {
    return chat.lastMessageAt.seconds * 1000
  }
  const last = chat.messages[chat.messages.length - 1]
  if (last?.timestamp != null) {
    if (typeof last.timestamp === 'string') {
      const d = new Date(last.timestamp)
      if (!Number.isNaN(d.getTime())) return d.getTime()
    } else if (typeof last.timestamp === 'object' && 'seconds' in last.timestamp) {
      return last.timestamp.seconds * 1000
    }
  }
  return chat.createdAt.seconds * 1000
}

function getChatLastActivityTimestamp(chat: Chat): TimestampValue {
  if (chat.lastMessageAt) return chat.lastMessageAt
  const last = chat.messages[chat.messages.length - 1]
  if (last?.timestamp) return last.timestamp
  return { seconds: chat.createdAt.seconds, nanoseconds: 0 }
}

function formatLastMessagePreview(content: string, maxLen = 240): string {
  const oneLine = content.replace(/\s+/g, ' ').trim()
  if (!oneLine) return '—'
  if (oneLine.length <= maxLen) return oneLine
  return `${oneLine.slice(0, maxLen).trimEnd()}…`
}

interface ProfileData {
  email?: string | null
  instagram?: string | null
  createdAt?: TimestampLike
  lastLogin?: TimestampLike
  deviceInfo?: {
    platform?: string | null
    deviceType?: string | null
    appVersion?: string | null
    webVersion?: string | null
    loggedAt?: TimestampLike
    userAgent?: string | null
  } | null
}

interface ProfileState {
  loading: boolean
  error: string | null
  data?: ProfileData
}

export function ChatGrid({
  chats,
  statusFilter,
  dateFilter,
  sortOrder = 'newest',
  onDeleteChat,
  bookingAnalyses = new Map(),
  allChatsForUserLookup,
  onNavigateToUserChat,
}: ChatGridProps) {
  const [expandedChats, setExpandedChats] = useState<Set<string>>(new Set())
  const [profileStates, setProfileStates] = useState<Record<string, ProfileState>>({})
  const [userChatsDialogOpen, setUserChatsDialogOpen] = useState(false)
  const [userChatsFocusUserId, setUserChatsFocusUserId] = useState<string | null>(null)
  const [userChatsLabel, setUserChatsLabel] = useState('')
  const [userChatsSourceChatId, setUserChatsSourceChatId] = useState<string | null>(null)

  const chatsForFocusedUser = useMemo(() => {
    if (!allChatsForUserLookup?.length || !userChatsFocusUserId) return []
    return [...allChatsForUserLookup]
      .filter(c => c.userId === userChatsFocusUserId)
      .sort((a, b) => getChatActivityMs(b) - getChatActivityMs(a))
  }, [allChatsForUserLookup, userChatsFocusUserId])

  const toDate = (value: TimestampLike): Date | null => {
    if (!value) return null
    if (value instanceof Date) return value
    if (typeof value === 'number') {
      const dateFromNumber = new Date(value)
      return Number.isNaN(dateFromNumber.getTime()) ? null : dateFromNumber
    }
    if (typeof value === 'string') {
      const dateFromString = new Date(value)
      return Number.isNaN(dateFromString.getTime()) ? null : dateFromString
    }
    if (typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
      const dateFromToDate = value.toDate()
      return dateFromToDate instanceof Date && !Number.isNaN(dateFromToDate.getTime()) ? dateFromToDate : null
    }
    if (typeof value === 'object' && 'seconds' in value && typeof value.seconds === 'number') {
      const dateFromSeconds = new Date(value.seconds * 1000)
      return Number.isNaN(dateFromSeconds.getTime()) ? null : dateFromSeconds
    }
    return null
  }

  const formatDetailedDate = (value: TimestampLike): string => {
    const date = toDate(value)
    if (!date) return 'Not provided'
    try {
      return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZoneName: 'shortOffset'
      }).format(date).replace('GMT', 'UTC')
    } catch {
      return 'Not provided'
    }
  }

  const formatInstagram = (value: string | null | undefined): string => {
    if (!value) return 'Not provided'
    return value.startsWith('@') ? value : `@${value}`
  }

  const getProfileKey = (type: 'user' | 'artist', chat: Chat): string =>
    `${type}:${type === 'user' ? (chat.userId || `anon-${chat.id}`) : chat.artistId}`

  const setProfileLoading = (key: string) => {
    setProfileStates(prev => ({
      ...prev,
      [key]: { loading: true, error: null, data: prev[key]?.data }
    }))
  }

  const setProfileLoaded = (key: string, data: ProfileData) => {
    setProfileStates(prev => ({
      ...prev,
      [key]: { loading: false, error: null, data }
    }))
  }

  const setProfileError = (key: string, message: string) => {
    setProfileStates(prev => ({
      ...prev,
      [key]: { loading: false, error: message }
    }))
  }

  const loadProfile = async (type: 'user' | 'artist', chat: Chat) => {
    const key = getProfileKey(type, chat)
    const existingState = profileStates[key]
    if (existingState?.loading || existingState?.data) return

    setProfileLoading(key)

    try {
      if (type === 'user') {
        if (!chat.userId) {
          setProfileLoaded(key, {
            email: chat.userEmail ?? null,
            instagram: chat.userInstagram ?? null,
            createdAt: undefined,
            lastLogin: undefined,
            deviceInfo: null,
          })
          return
        }
        const userDoc = await getDoc(doc(db, 'users', chat.userId))
        const userData = userDoc.exists() ? userDoc.data() : null

        setProfileLoaded(key, {
          email: (userData?.email as string | undefined) ?? chat.userEmail ?? null,
          instagram: (userData?.instagram as string | undefined) ?? chat.userInstagram ?? null,
          createdAt: userData?.createdAt,
          lastLogin: userData?.lastLogin ?? userData?.lastSignIn,
          deviceInfo: (userData?.deviceInfo as ProfileData['deviceInfo']) ?? null
        })
        return
      }

      const artistDoc = await getDoc(doc(db, 'artists', chat.artistId))
      const artistData = artistDoc.exists() ? artistDoc.data() : null
      const artistUserId = artistData?.userId as string | undefined

      let artistUserData: Record<string, any> | null = null
      if (artistUserId) {
        const artistUserDoc = await getDoc(doc(db, 'users', artistUserId))
        artistUserData = artistUserDoc.exists() ? artistUserDoc.data() : null
      }

      setProfileLoaded(key, {
        email: (artistUserData?.email as string | undefined) ?? null,
        instagram: (artistUserData?.instagram as string | undefined) ?? (artistData?.displayName as string | undefined) ?? chat.artistName ?? null,
        createdAt: artistUserData?.createdAt ?? artistData?.createdAt,
        lastLogin: artistUserData?.lastLogin ?? artistUserData?.lastSignIn,
        deviceInfo: (artistUserData?.deviceInfo as ProfileData['deviceInfo']) ?? null
      })
    } catch (error) {
      console.error('[ChatGrid] Failed loading profile', { type, chatId: chat.id, error })
      setProfileError(key, 'Failed to load profile data')
    }
  }

  const parseTimestamp = (value: TimestampValue): Date | null => {
    if (!value) return null

    if (typeof value === 'string') {
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? null : date
    }

    if ('seconds' in value && typeof value.seconds === 'number') {
      const date = new Date(value.seconds * 1000)
      return Number.isNaN(date.getTime()) ? null : date
    }

    return null
  }

  const formatDateTime = (value: TimestampValue): string => {
    const date = parseTimestamp(value)
    if (!date) return '-'

    try {
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
    } catch {
      return '-'
    }
  }

  // Format date to European format (DD/MM/YYYY HH:mm)
  const formatDate = (dateStr: string | null | undefined): string => formatDateTime(dateStr)

  // Format timestamp from seconds to European format
  const formatTimestamp = (seconds: number): string => {
    return formatDateTime({ seconds, nanoseconds: 0 })
  }

  const toggleExpanded = (chatId: string) => {
    setExpandedChats(prev => {
      const next = new Set(prev)
      if (next.has(chatId)) {
        next.delete(chatId)
      } else {
        next.add(chatId)
      }
      return next
    })
  }

  // Group by calendar month of last activity (lastMessageAt / last message / created fallback)
  const groupChatsByMonth = (chats: Chat[]) => {
    const grouped = chats.reduce((acc, chat) => {
      const date = new Date(getChatActivityMs(chat))
      const monthKey = date.toLocaleString('en-GB', { month: 'long', year: 'numeric' })
      if (!acc[monthKey]) {
        acc[monthKey] = []
      }
      acc[monthKey].push(chat)
      return acc
    }, {} as Record<string, Chat[]>)

    Object.keys(grouped).forEach(month => {
      grouped[month].sort((a, b) => {
        const timeA = getChatActivityMs(a)
        const timeB = getChatActivityMs(b)
        return sortOrder === 'newest' ? timeB - timeA : timeA - timeB
      })
    })

    // Sort months
    const sortedMonths = Object.keys(grouped).sort((a, b) => {
      const [monthA, yearA] = a.split(' ')
      const [monthB, yearB] = b.split(' ')
      const dateA = new Date(`${monthA} 1, ${yearA}`)
      const dateB = new Date(`${monthB} 1, ${yearB}`)
      return sortOrder === 'newest' ? dateB.getTime() - dateA.getTime() : dateA.getTime() - dateB.getTime()
    })

    return { grouped, sortedMonths }
  }

  // Emails to exclude
  const EXCLUDED_EMAILS = [
    'keijo@getmoretats.com',
    'jari@getmoretats.com',
    'lotta@getmoretats.com',
    'keijokristian.kunnari@gmail.com'
  ]

  // Filter chats
  const filteredChats = chats.filter(chat => {
    // Exclude chats with specific emails
    if (chat.userEmail && EXCLUDED_EMAILS.includes(chat.userEmail.toLowerCase())) {
      return false
    }
    const lastMessage = chat.messages[chat.messages.length - 1]
    let status: string | null = null

    if (chat.messages.length > 0) {
      if (lastMessage?.senderType === 'assistant') {
        status = 'waiting_artist'
      } else if (lastMessage?.senderType === 'artist') {
        status = 'waiting_user'
      } else if (lastMessage?.senderType === 'user') {
        status = 'waiting_artist'
      }
    } else {
      status = chat.status ?? null
    }

    const createdDate = new Date(chat.createdAt.seconds * 1000)
    const now = new Date()
    const daysDiff = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24))

    const matchesStatus = statusFilter === 'all' || status === statusFilter
    const matchesDate = dateFilter === 'all' ||
      (dateFilter === 'today' && daysDiff === 0) ||
      (dateFilter === 'week' && daysDiff <= 7) ||
      (dateFilter === 'month' && daysDiff <= 30)

    return matchesStatus && matchesDate
  })

  const { grouped, sortedMonths } = groupChatsByMonth(filteredChats)

  return (
    <div className="space-y-4">
      <div className="space-y-8">
        {sortedMonths.map((month: string) => (
          <div key={month} className="relative">
            <div className="sticky top-0 z-10 -ml-2 mb-4 flex items-center">
              <div className="bg-background/95 backdrop-blur-sm px-2 py-1 text-lg font-semibold text-muted-foreground">
                {month}
              </div>
              <div className="ml-4 h-px flex-1 bg-border/60" />
            </div>
            
            <div className="space-y-3">
              {grouped[month].map((chat: Chat) => {
                const isExpanded = expandedChats.has(chat.id)
                const lastMessage = chat.messages[chat.messages.length - 1]
                
                // DEBUG: Log exit risk for troubleshooting
                if (chat.platformExitLikelihood != null) {
                  console.log('🚪 Exit Risk Found:', { chatId: chat.id, exitRisk: chat.platformExitLikelihood })
                }
                
                const artistHasMessaged = chat.messages.some((m) => m.senderType === 'artist')
                const appointmentRaw = chat.appointmentDate || chat.aiBookingDate
                const appointmentParsed = appointmentRaw ? parseAppointmentCalendarDate(appointmentRaw) : null
                const appointmentIsAiOnly = Boolean(
                  chat.aiBookingDetected && chat.aiBookingDate && !chat.appointmentDate
                )
                const appointmentConfidence = bookingAnalyses.get(chat.id)?.confidence

                // Determine waiting status based on last message sender
                let waitingStatus = null
                if (lastMessage?.senderType === 'assistant') {
                  waitingStatus = 'waiting_artist'
                } else if (lastMessage?.senderType === 'artist') {
                  waitingStatus = 'waiting_user'
                } else if (lastMessage?.senderType === 'user') {
                  // User spoke last → artist turn (include new threads before hasLead is set)
                  waitingStatus = 'waiting_artist'
                }

                return (
                  <div 
                    key={chat.id}
                    id={`chat-grid-row-${chat.id}`}
                    className="border border-border bg-card hover:bg-card/50 transition-colors rounded-lg overflow-hidden scroll-mt-4"
                  >
                    {/* Top row: identity → last message (collapsed) → analysis & actions */}
                    <div
                      className={cn(
                        'grid gap-3 p-3 lg:items-start lg:gap-4',
                        isExpanded
                          ? 'grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto]'
                          : 'grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(11rem,1.1fr)_auto]'
                      )}
                    >
                      <div className="flex min-w-0 flex-col gap-2">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button
                                className="text-sm font-semibold text-blue-600 hover:underline sm:text-base"
                                onClick={() => void loadProfile('artist', chat)}
                              >
                                {chat.artistName}
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              {(() => {
                                const state = profileStates[getProfileKey('artist', chat)]
                                return (
                              <AlertDialogHeader>
                                <AlertDialogTitle>Artist Details - {chat.artistName}</AlertDialogTitle>
                                <AlertDialogDescription asChild>
                                  <div className="space-y-4 mt-2">
                                    {state?.loading ? (
                                      <div className="text-sm text-muted-foreground">Loading profile data...</div>
                                    ) : state?.error ? (
                                      <div className="text-sm text-destructive">{state.error}</div>
                                    ) : (
                                      <div className="grid grid-cols-2 gap-2 text-sm">
                                        <div className="font-medium">Email:</div>
                                        <div>{state?.data?.email || 'Not provided'}</div>

                                        <div className="font-medium">Instagram:</div>
                                        <div>{formatInstagram(state?.data?.instagram)}</div>

                                        <div className="font-medium">Created At:</div>
                                        <div>{formatDetailedDate(state?.data?.createdAt)}</div>

                                        <div className="font-medium">Last Login:</div>
                                        <div>{formatDetailedDate(state?.data?.lastLogin)}</div>

                                        <div className="font-medium">Platform:</div>
                                        <div>{state?.data?.deviceInfo?.platform || 'Not provided'}</div>

                                        <div className="font-medium">Device Type:</div>
                                        <div>{state?.data?.deviceInfo?.deviceType || 'Not provided'}</div>

                                        <div className="font-medium">App Version:</div>
                                        <div>{state?.data?.deviceInfo?.appVersion ?? 'Not provided'}</div>

                                        <div className="font-medium">Web Version:</div>
                                        <div>{state?.data?.deviceInfo?.webVersion || 'Not provided'}</div>

                                        <div className="font-medium">Logged At:</div>
                                        <div>{formatDetailedDate(state?.data?.deviceInfo?.loggedAt)}</div>

                                        <div className="font-medium">User Agent:</div>
                                        <div className="break-all text-xs">{state?.data?.deviceInfo?.userAgent || 'Not provided'}</div>
                                      </div>
                                    )}
                                  </div>
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                                )
                              })()}
                              <AlertDialogFooter>
                                <AlertDialogCancel>Close</AlertDialogCancel>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          <span className="text-muted-foreground text-xs sm:text-sm">→</span>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button
                                className="text-sm font-semibold text-purple-600 hover:underline sm:text-base"
                                onClick={() => void loadProfile('user', chat)}
                              >
                                {chat.userInstagram?.trim()
                                  ? `@${chat.userInstagram.replace(/^@/, '')}`
                                  : 'Client'}
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              {(() => {
                                const state = profileStates[getProfileKey('user', chat)]
                                const clientTitle = chat.userInstagram?.trim()
                                  ? `@${chat.userInstagram.replace(/^@/, '')}`
                                  : 'Client (no linked user)'
                                return (
                              <AlertDialogHeader>
                                <AlertDialogTitle>User Details — {clientTitle}</AlertDialogTitle>
                                <AlertDialogDescription asChild>
                                  <div className="space-y-4 mt-2">
                                    {state?.loading ? (
                                      <div className="text-sm text-muted-foreground">Loading profile data...</div>
                                    ) : state?.error ? (
                                      <div className="text-sm text-destructive">{state.error}</div>
                                    ) : (
                                      <div className="grid grid-cols-2 gap-2 text-sm">
                                        <div className="font-medium">Email:</div>
                                        <div>{state?.data?.email || 'Not provided'}</div>

                                        <div className="font-medium">Instagram:</div>
                                        <div>{formatInstagram(state?.data?.instagram)}</div>

                                        <div className="font-medium">Created At:</div>
                                        <div>{formatDetailedDate(state?.data?.createdAt)}</div>

                                        <div className="font-medium">Last Login:</div>
                                        <div>{formatDetailedDate(state?.data?.lastLogin)}</div>

                                        <div className="font-medium">Platform:</div>
                                        <div>{state?.data?.deviceInfo?.platform || 'Not provided'}</div>

                                        <div className="font-medium">Device Type:</div>
                                        <div>{state?.data?.deviceInfo?.deviceType || 'Not provided'}</div>

                                        <div className="font-medium">App Version:</div>
                                        <div>{state?.data?.deviceInfo?.appVersion ?? 'Not provided'}</div>

                                        <div className="font-medium">Web Version:</div>
                                        <div>{state?.data?.deviceInfo?.webVersion || 'Not provided'}</div>

                                        <div className="font-medium">Logged At:</div>
                                        <div>{formatDetailedDate(state?.data?.deviceInfo?.loggedAt)}</div>

                                        <div className="font-medium">User Agent:</div>
                                        <div className="break-all text-xs">{state?.data?.deviceInfo?.userAgent || 'Not provided'}</div>
                                      </div>
                                    )}
                                  </div>
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                                )
                              })()}
                              <AlertDialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
                                {allChatsForUserLookup && allChatsForUserLookup.length > 0 && chat.userId ? (
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    className="w-full sm:w-auto"
                                    onClick={() => {
                                      setUserChatsSourceChatId(chat.id)
                                      setUserChatsFocusUserId(chat.userId)
                                      setUserChatsLabel(chat.userInstagram?.trim() || chat.userId)
                                      setUserChatsDialogOpen(true)
                                    }}
                                  >
                                    Chats
                                  </Button>
                                ) : null}
                                <AlertDialogCancel className="w-full sm:w-auto mt-0">Close</AlertDialogCancel>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          {!artistHasMessaged && (
                            <span
                              className="shrink-0 rounded-md border border-amber-500/70 bg-gradient-to-b from-amber-100 to-amber-200/90 px-1.5 py-0.5 text-[10px] font-semibold text-amber-950 shadow-sm ring-1 ring-amber-400/40 dark:border-amber-500/50 dark:from-amber-950/80 dark:to-amber-900/70 dark:text-amber-100 dark:ring-amber-400/30 sm:text-xs"
                              title="Artist has not sent a message in this chat yet"
                            >
                              New client
                            </span>
                          )}
                          {chat.bookingOnly && (
                            <span
                              className="shrink-0 rounded-md border border-violet-500/60 bg-gradient-to-b from-violet-100 to-violet-200/90 px-1.5 py-0.5 text-[10px] font-semibold text-violet-950 shadow-sm ring-1 ring-violet-400/35 dark:border-violet-500/50 dark:from-violet-950/80 dark:to-violet-900/70 dark:text-violet-100 dark:ring-violet-400/30 sm:text-xs"
                              title="Time4Tats manual booking — no chat thread in Firestore"
                            >
                              Booking only
                            </span>
                          )}
                        </div>
                        {chat.userEmail && (
                          <div className="text-xs text-muted-foreground">{chat.userEmail}</div>
                        )}
                        <div
                          className="text-xs text-muted-foreground tabular-nums"
                          title="Last message / activity time (from analytics)"
                        >
                          Last activity:{' '}
                          {formatDateTime({
                            seconds: Math.floor(getChatActivityMs(chat) / 1000),
                            nanoseconds: 0,
                          })}
                        </div>
                        <ChatStatusFlow
                          waitingStatus={waitingStatus}
                          platformExitLikelihood={chat.platformExitLikelihood}
                          lastMessage={lastMessage}
                        />
                      </div>

                      {!isExpanded && (
                        <div
                          className={cn(
                            'min-w-0 rounded-lg p-3 lg:min-h-0',
                            'border border-border/70 bg-gradient-to-br from-muted/60 via-muted/30 to-background shadow-[inset_0_1px_0_0_hsl(var(--border))]'
                          )}
                        >
                          {lastMessage ? (
                            <div className="min-w-0">
                              <div className="mb-2 flex flex-wrap items-center gap-2">
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                  Last message
                                </span>
                                <span
                                  className={cn(
                                    'inline-flex shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold capitalize ring-1 ring-inset',
                                    lastMessage.senderType === 'user' &&
                                      'bg-purple-500/12 text-purple-800 ring-purple-500/25 dark:text-purple-200 dark:ring-purple-400/30',
                                    lastMessage.senderType === 'artist' &&
                                      'bg-sky-500/12 text-sky-900 ring-sky-500/25 dark:text-sky-100 dark:ring-sky-400/30',
                                    lastMessage.senderType === 'assistant' &&
                                      'bg-zinc-500/12 text-zinc-800 ring-zinc-500/20 dark:text-zinc-200 dark:ring-zinc-500/35'
                                  )}
                                >
                                  {lastMessage.senderType}
                                </span>
                              </div>
                              <p className="text-sm leading-relaxed text-foreground/95 line-clamp-3 [overflow-wrap:anywhere]">
                                {formatLastMessagePreview(lastMessage.content)}
                              </p>
                            </div>
                          ) : (
                            <div className="text-sm text-muted-foreground italic">No messages yet</div>
                          )}
                        </div>
                      )}

                      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 sm:flex-nowrap lg:justify-self-end">
                        <div className="flex items-center gap-0.5">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleExpanded(chat.id)}
                            className="h-7 w-7 p-0"
                          >
                            {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  {chat.bookingOnly ? 'Delete booking' : 'Delete Chat'}
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  {chat.bookingOnly
                                    ? 'Remove this Time4Tats manual booking from the calendar? This does not delete a chat thread (there is none).'
                                    : 'Are you sure you want to delete this chat? This action cannot be undone.'}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => onDeleteChat(chat.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </div>

                    {/* Second row: timeline; summary only when expanded */}
                    <div
                      className={cn(
                        'px-4 pb-4',
                        isExpanded && 'flex flex-col items-start gap-6 sm:flex-row sm:gap-8'
                      )}
                    >
                      <div className={cn('min-w-0', isExpanded && 'flex-1')}>
                        <div className="space-y-2">
                          {/* Timeline */}
                          <div className="flex items-center gap-2">
                            {/* Step 1: Created */}
                            <div className="flex flex-col items-start">
                              <div className="text-xs font-medium text-muted-foreground mb-1">Created</div>
                              <div className="text-sm text-foreground">{formatTimestamp(chat.createdAt.seconds)}</div>
                            </div>

                            <div className="w-8 h-px bg-border mx-1 mt-3" />

                            {/* Step 2: Offer Sent */}
                            <div className="flex flex-col items-start">
                              <div className="text-xs font-medium text-muted-foreground mb-1">Offer Sent</div>
                              <div className={cn(
                                "text-sm",
                                chat.offerSentDate ? "text-foreground font-medium" : "text-muted-foreground"
                              )}>
                                {formatDate(chat.offerSentDate)}
                              </div>
                            </div>

                            <div className="w-8 h-px bg-border mx-1 mt-3" />

                            {/* Step 3: Accepted */}
                            <div className="flex flex-col items-start">
                              <div className="text-xs font-medium text-muted-foreground mb-1">Accepted</div>
                              <div className={cn(
                                "text-sm",
                                chat.offerAcceptedDate ? "text-green-700 font-medium dark:text-green-400" : "text-muted-foreground"
                              )}>
                                {formatDate(chat.offerAcceptedDate)}
                              </div>
                            </div>

                            <div className="w-8 h-px bg-border mx-1 mt-3" />

                            {/* Step 4: Appointment */}
                            <div className="flex flex-col items-start min-w-0 max-w-[11rem] sm:max-w-none">
                              <div className="text-xs font-medium text-muted-foreground mb-1">Appointment</div>
                              {!appointmentRaw ? (
                                <div className="text-sm text-muted-foreground">—</div>
                              ) : (
                                <div className="flex flex-col items-start gap-0.5">
                                  <div className="flex flex-wrap items-center gap-1.5">
                                    <span
                                      className={cn(
                                        'text-sm',
                                        appointmentParsed && isTime4TatsEventDay(appointmentParsed)
                                          ? 'time4tats-btn text-filter-pill relative z-[2] inline-flex rounded-md px-2 py-0.5 !text-xs font-semibold'
                                          : 'text-purple-700 font-semibold dark:text-purple-400'
                                      )}
                                      title={
                                        appointmentParsed && isTime4TatsEventDay(appointmentParsed)
                                          ? 'time4tats event day'
                                          : undefined
                                      }
                                    >
                                      {formatDate(appointmentRaw)}
                                    </span>
                                    {appointmentIsAiOnly && (
                                      <>
                                        <span
                                          className="shrink-0 rounded border border-amber-500/45 bg-amber-100/85 px-1 py-px text-[9px] font-bold uppercase tracking-wide text-amber-950 dark:bg-amber-950/55 dark:text-amber-100"
                                          title="Date from AI analysis"
                                        >
                                          AI
                                        </span>
                                        {appointmentConfidence != null && (
                                          <span className="text-[10px] tabular-nums text-muted-foreground">
                                            {Math.round(appointmentConfidence * 100)}%
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="min-w-0 flex-[1.5] rounded-lg bg-muted/30 p-3 sm:max-w-xl lg:max-w-md">
                          {bookingAnalyses?.has(chat.id) && bookingAnalyses.get(chat.id)?.summary ? (
                            <div className="text-sm">
                              <div className="mb-1 text-xs font-medium text-muted-foreground">Summary:</div>
                              <div className="line-clamp-3 text-foreground/90">
                                {bookingAnalyses.get(chat.id)?.summary}
                              </div>
                            </div>
                          ) : (
                            <div className="text-sm italic text-muted-foreground">No summary available</div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Expanded Section - Chat History */}
                    {isExpanded && (
                      <div className="border-t border-border bg-muted/30 p-4">
                        <div className="text-sm font-medium text-foreground mb-3">Full Chat History</div>
                        <div className="space-y-2 max-h-[500px] overflow-y-auto">
                          {[...chat.messages].reverse().map((message, index) => (
                            <div key={index} className="p-3 bg-card rounded-lg border border-border">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <div className="font-medium text-xs text-muted-foreground">
                                  {message.senderType} - {formatDateTime(message.timestamp)}
                                </div>
                                <div className="flex items-center gap-2">
                                  {/* Notification Status indicator - shown for messages with notification tracking */}
                                  {message.notification && (
                                    <div className={cn(
                                      "text-xs font-medium px-2 py-0.5 rounded-full",
                                      message.notification.status === 'pending'
                                        ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400"
                                        : message.notification.status === 'sent'
                                        ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400"
                                        : message.notification.status === 'received'
                                        ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400"
                                        : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                                    )}>
                                      {message.notification.status === 'pending' && '🕐 Pending'}
                                      {message.notification.status === 'sent' && '🔔 Sent'}
                                      {message.notification.status === 'received' && '📱 Received'}
                                      {!['pending', 'sent', 'received'].includes(message.notification.status) && `📌 ${message.notification.status}`}
                                    </div>
                                  )}
                                  {/* Fallback for legacy notificationSent boolean */}
                                  {!message.notification && message.notificationSent !== undefined && (
                                    <div className={cn(
                                      "text-xs font-medium px-2 py-0.5 rounded-full",
                                      message.notificationSent
                                        ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400"
                                        : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                                    )}>
                                      {message.notificationSent ? '🔔 Notif Sent' : '🔕 No Notif'}
                                    </div>
                                  )}
                                  {/* Read indicator - only shown for user and artist messages */}
                                  {message.senderType !== 'assistant' && (
                                    <div className={cn(
                                      "text-xs font-medium px-2 py-0.5 rounded-full",
                                      message.isRead
                                        ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400"
                                        : "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400"
                                    )}>
                                      {message.isRead ? '✓ Read' : 'Unread'}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="text-sm whitespace-pre-wrap break-words">
                                {message.type === 'image' && !message.content ? 'Image' : message.content}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      <Dialog
        open={userChatsDialogOpen}
        onOpenChange={open => {
          setUserChatsDialogOpen(open)
          if (!open) {
            setUserChatsFocusUserId(null)
            setUserChatsSourceChatId(null)
            setUserChatsLabel('')
          }
        }}
      >
        <DialogContent className="z-[100] flex max-h-[min(85vh,640px)] max-w-lg flex-col gap-0 p-0">
          <DialogHeader className="shrink-0 border-b border-border px-6 py-4 text-left">
            <DialogTitle>Chats</DialogTitle>
            {userChatsLabel ? (
              <DialogDescription>
                @{userChatsLabel.replace(/^@/, '')} · {chatsForFocusedUser.length} in this load
              </DialogDescription>
            ) : null}
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
            {chatsForFocusedUser.length === 0 ? (
              <p className="text-sm text-muted-foreground">No chats for this user in the current analytics dataset.</p>
            ) : (
              <ul className="space-y-2">
                {chatsForFocusedUser.map(c => (
                  <li
                    key={c.id}
                    className="flex flex-col gap-2 rounded-lg border border-border bg-card/50 p-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span className="font-medium truncate">{c.artistName ?? 'Artist'}</span>
                        {userChatsSourceChatId === c.id ? (
                          <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                            This chat
                          </span>
                        ) : null}
                      </div>
                      <div className="text-xs text-muted-foreground tabular-nums">
                        Last activity · {formatDateTime(getChatLastActivityTimestamp(c))}
                      </div>
                      <div className="text-xs capitalize text-muted-foreground">{c.status.replace(/_/g, ' ')}</div>
                    </div>
                    {onNavigateToUserChat ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        className="shrink-0 self-start sm:self-center"
                        onClick={() => {
                          onNavigateToUserChat({ chatId: c.id, artistId: c.artistId })
                          setUserChatsDialogOpen(false)
                        }}
                      >
                        Show in list
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="shrink-0 border-t border-border px-6 py-3">
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => setUserChatsDialogOpen(false)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
